// src/features/marketResearch/components/sections/Recommendations.tsx
import React from 'react';
import {
  Box,
  Chip,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  Business,
  Assessment,
  Schedule,
  CheckCircle,
  Star,
  Security,
  AccountBalance,
  Timeline as TimelineIcon,
  Recommend
} from '@mui/icons-material';
import { Section, SubSectionTitle } from './Section';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

interface RecData {
  pricingStrategy?: string;
  targetSegments?: string[];
  marketingFocus?: string[];
  recommendations?: Array<{
    name?: string;
    description?: string;
    potentialMarketImpact?: string;
    estimatedCompletionDate?: string;
  }>;
}

// Type for a single recommendation object
interface SingleRecommendation {
  name?: string;
  description?: string;
  potentialMarketImpact?: string;
  estimatedCompletionDate?: string;
}

interface Props {
  data?: RecData | SingleRecommendation | SingleRecommendation[] | any;
}

// Helper function to safely convert any value to string
const safeToString = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '[Object]';
    }
  }
  return String(value);
};

// Helper function to check if an object is a single recommendation
const isSingleRecommendation = (obj: any): obj is SingleRecommendation => {
  return obj && typeof obj === 'object' && !Array.isArray(obj) &&
    ('name' in obj || 'description' in obj || 'potentialMarketImpact' in obj || 'estimatedCompletionDate' in obj);
};

// Helper function to check if an object is RecData
const isRecData = (obj: any): obj is RecData => {
  return obj && typeof obj === 'object' && !Array.isArray(obj) &&
    ('pricingStrategy' in obj || 'targetSegments' in obj || 'marketingFocus' in obj || 'recommendations' in obj);
};

export const Recommendations: React.FC<Props> = ({ data }) => {
  // Debug log to see what data structure we're receiving
  console.log('Recommendations data received:', data);

  // Access MUI theme (must be called before any early returns to comply with React Hooks rules)
  const theme = useTheme();

  // If no data, return empty recommendations section
  if (!data) {
    return (
      <Section
        id="recommendations"
        title="Strategic Recommendations & Implementation Roadmap"
        icon={<Recommend />}
      >
        <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
          <Typography variant="body1" color="text.secondary">
            No recommendations data available.
          </Typography>
        </Paper>
      </Section>
    );
  }

  // Handle single recommendation object
  if (isSingleRecommendation(data)) {
    const singleRec = data as SingleRecommendation;
    return (
      <Section
        id="recommendations"
        title="Strategic Recommendations & Implementation Roadmap"
        icon={<Recommend />}
      >
        <Card>
          <CardContent>
            <Typography variant="h6">{safeToString(singleRec.name || 'Recommendation')}</Typography>
            <Typography variant="body2">{safeToString(singleRec.description)}</Typography>
            {singleRec.potentialMarketImpact && (
              <Typography variant="caption" display="block">Impact: {safeToString(singleRec.potentialMarketImpact)}</Typography>
            )}
            {singleRec.estimatedCompletionDate && (
              <Typography variant="caption" display="block">Timeline: {safeToString(singleRec.estimatedCompletionDate)}</Typography>
            )}
          </CardContent>
        </Card>
      </Section>
    );
  }

  // Handle array of recommendations
  if (Array.isArray(data)) {
    return (
      <Section
        id="recommendations"
        title="Strategic Recommendations & Implementation Roadmap"
        icon={<Recommend />}
      >
        <Box sx={{ mb: 4 }}>
          <SubSectionTitle>Market-Specific Recommendations</SubSectionTitle>
          <Grid container spacing={2}>
            {data.map((rec, index) => {
              // Handle both string and object recommendations
              if (typeof rec === 'string') {
                return (
                  <Grid item xs={12} md={6} key={index}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {rec}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              }

              // Handle object recommendations
              return (
                <Grid item xs={12} md={6} key={index}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        {safeToString(rec?.name || `Recommendation ${index + 1}`)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {safeToString(rec?.description)}
                      </Typography>
                      {rec?.potentialMarketImpact && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Market Impact: {safeToString(rec.potentialMarketImpact)}
                        </Typography>
                      )}
                      {rec?.estimatedCompletionDate && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Timeline: {safeToString(rec.estimatedCompletionDate)}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Section>
    );
  }

  // Handle the standard RecData structure
  let recData: RecData = {};

  if (isRecData(data)) {
    recData = data;
  } else if (typeof data === 'object' && data !== null) {
    // Try to extract RecData fields from any object
    recData = {
      pricingStrategy: data.pricingStrategy,
      targetSegments: data.targetSegments,
      marketingFocus: data.marketingFocus,
      recommendations: data.recommendations
    };
  }

  const pricingStrategy = recData.pricingStrategy || '';
  const targetSegments = Array.isArray(recData.targetSegments) ? recData.targetSegments : [];
  const marketingFocus = Array.isArray(recData.marketingFocus) ? recData.marketingFocus : [];
  const incomingRecommendations = Array.isArray(recData.recommendations) ? recData.recommendations : [];

  // Safely extract recommendation data
  const extractRecommendationText = (recommendations: any[]) => {
    if (!Array.isArray(recommendations)) return [];

    return recommendations.map(rec => {
      if (typeof rec === 'string') return rec;
      if (typeof rec === 'object' && rec !== null) {
        return rec.name || rec.description || JSON.stringify(rec);
      }
      return String(rec);
    });
  };

  const recommendationTexts = extractRecommendationText(incomingRecommendations);

  // Strategic recommendations with financial impact
  const strategicRecommendations = [
    {
      title: 'Revenue Optimization Strategy',
      description: 'Implement dynamic pricing and yield management systems to maximize ADR',
      priority: 'High',
      timeline: '3-6 months',
      investment: '$150K - $250K',
      expectedROI: '15-25%',
      icon: <AttachMoney />,
      color: '#4caf50',
      details: [
        'Deploy advanced revenue management system',
        'Implement demand-based pricing algorithms',
        'Enhance channel distribution strategy',
        'Optimize booking patterns and lead times'
      ]
    },
    {
      title: 'Market Positioning Enhancement',
      description: 'Strengthen competitive position through targeted service improvements',
      priority: 'High',
      timeline: '6-12 months',
      investment: '$300K - $500K',
      expectedROI: '20-30%',
      icon: <Star />,
      color: '#1976d2',
      details: [
        'Upgrade key guest-facing amenities',
        'Enhance digital guest experience',
        'Implement loyalty program enhancements',
        'Strengthen brand positioning in market'
      ]
    },
    {
      title: 'Operational Efficiency Initiative',
      description: 'Streamline operations to improve margins and guest satisfaction',
      priority: 'Medium',
      timeline: '4-8 months',
      investment: '$100K - $200K',
      expectedROI: '12-18%',
      icon: <Business />,
      color: '#ff9800',
      details: [
        'Optimize staffing models and scheduling',
        'Implement energy management systems',
        'Enhance procurement and vendor management',
        'Deploy automation where appropriate'
      ]
    },
    {
      title: 'Market Expansion Strategy',
      description: 'Capture untapped market segments and increase market share',
      priority: 'Medium',
      timeline: '12-18 months',
      investment: '$200K - $350K',
      expectedROI: '18-25%',
      icon: <TrendingUp />,
      color: '#9c27b0',
      details: [
        'Develop corporate group sales program',
        'Expand leisure market penetration',
        'Enhance conference and events capabilities',
        'Implement targeted marketing campaigns'
      ]
    }
  ];

  // Implementation timeline steps
  const implementationSteps = [
    {
      phase: 'Phase 1: Foundation (Months 1-6)',
      activities: [
        'Revenue management system implementation',
        'Staff training and development programs',
        'Initial market positioning adjustments',
        'Baseline performance metrics establishment'
      ]
    },
    {
      phase: 'Phase 2: Enhancement (Months 7-12)',
      activities: [
        'Amenity upgrades and facility improvements',
        'Advanced pricing strategy deployment',
        'Enhanced guest experience programs',
        'Competitive positioning strengthening'
      ]
    },
    {
      phase: 'Phase 3: Expansion (Months 13-18)',
      activities: [
        'Market share expansion initiatives',
        'New revenue stream development',
        'Strategic partnership establishment',
        'Long-term growth strategy execution'
      ]
    }
  ];

  // Financial projections table
  const financialProjections = [
    { metric: 'Revenue Growth', year1: '8-12%', year2: '12-18%', year3: '15-22%' },
    { metric: 'ADR Improvement', year1: '5-8%', year2: '10-15%', year3: '12-18%' },
    { metric: 'Occupancy Optimization', year1: '3-5%', year2: '5-8%', year3: '7-10%' },
    { metric: 'Operating Margin', year1: '2-4%', year2: '4-7%', year3: '6-9%' },
    { metric: 'ROI on Investments', year1: '12-15%', year2: '18-25%', year3: '22-30%' }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return '#f44336';
      case 'Medium': return '#ff9800';
      case 'Low': return '#4caf50';
      default: return '#666';
    }
  };

  return (
    <Section
      id="recommendations"
      title="Strategic Recommendations & Implementation Roadmap"
      icon={<Recommend />}
    >
      {/* Error boundary for the entire component */}
      {(() => {
        try {
          return (
            <>
              {/* Executive Recommendations Overview */}
              <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Recommend sx={{ fontSize: 32, color: '#1976d2', mr: 2 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      Investment Strategy Overview
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Strategic initiatives designed to maximize asset value and market performance
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <AttachMoney sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
                        $750K - $1.3M
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Investment Required
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <TrendingUp sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                        18-25%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Expected ROI (3-Year)
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Schedule sx={{ fontSize: 48, color: '#ff9800', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800' }}>
                        18 Months
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Full Implementation Timeline
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Display target segments and marketing focus if available */}
              {(targetSegments.length > 0 || marketingFocus.length > 0) && (
                <Box sx={{ mb: 4 }}>
                  <SubSectionTitle>Strategic Focus Areas</SubSectionTitle>
                  <Grid container spacing={3}>
                    {targetSegments.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                              Target Segments
                            </Typography>
                            <List dense>
                              {targetSegments.map((segment, index) => (
                                <ListItem key={index}>
                                  <ListItemIcon>
                                    <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={safeToString(segment)}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {marketingFocus.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                              Marketing Focus
                            </Typography>
                            <List dense>
                              {marketingFocus.map((focus, index) => (
                                <ListItem key={index}>
                                  <ListItemIcon>
                                    <CheckCircle sx={{ fontSize: 16, color: '#1976d2' }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={safeToString(focus)}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Display actual recommendations from data if available */}
              {incomingRecommendations.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SubSectionTitle>Market-Specific Recommendations</SubSectionTitle>
                  <Grid container spacing={2}>
                    {incomingRecommendations.map((rec, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Card sx={{ height: '100%' }}>
                          <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                              {safeToString(typeof rec === 'object' && rec?.name ? rec.name : `Recommendation ${index + 1}`)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {safeToString(typeof rec === 'object' && rec?.description ? rec.description : rec)}
                            </Typography>
                            {typeof rec === 'object' && rec?.potentialMarketImpact && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Market Impact: {safeToString(rec.potentialMarketImpact)}
                              </Typography>
                            )}
                            {typeof rec === 'object' && rec?.estimatedCompletionDate && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Timeline: {safeToString(rec.estimatedCompletionDate)}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Strategic Recommendations Cards */}
              <Box sx={{ mb: 4 }}>
                <SubSectionTitle>Core Strategic Initiatives</SubSectionTitle>
                <Grid container spacing={3}>
                  {strategicRecommendations.map((rec, index) => (
                    <Grid item xs={12} lg={6} key={index}>
                      <Card
                        sx={{
                          height: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                            <Box sx={{
                              p: 1.5,
                              borderRadius: 2,
                              backgroundColor: `${rec.color}15`,
                              color: rec.color
                            }}>
                              {rec.icon}
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                  {rec.title}
                                </Typography>
                                <Chip
                                  label={rec.priority}
                                  size="small"
                                  sx={{
                                    backgroundColor: `${getPriorityColor(rec.priority)}15`,
                                    color: getPriorityColor(rec.priority),
                                    fontWeight: 500
                                  }}
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {rec.description}
                              </Typography>
                            </Box>
                          </Box>

                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Timeline
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {rec.timeline}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Investment
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {rec.investment}
                              </Typography>
                            </Grid>
                          </Grid>

                          <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Expected ROI
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: rec.color }}>
                              {rec.expectedROI}
                            </Typography>
                          </Box>

                          <List dense>
                            {rec.details.slice(0, 3).map((detail, idx) => (
                              <ListItem key={idx} sx={{ px: 0, py: 0.25 }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <CheckCircle sx={{ fontSize: 16, color: rec.color }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={detail}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Implementation Timeline */}
              <Paper sx={{ p: 4, mb: 4, border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
                <SubSectionTitle>Implementation Roadmap</SubSectionTitle>
                <Box sx={{ mt: 3 }}>
                  {implementationSteps.map((step, index) => (
                    <Card key={index} sx={{ mb: 3, border: `1px solid ${theme.palette.divider}` }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                          {step.phase}
                        </Typography>
                        <Grid container spacing={2}>
                          {step.activities.map((activity, idx) => (
                            <Grid item xs={12} sm={6} key={idx}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />
                                <Typography variant="body2">{activity}</Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Paper>

              {/* Financial Projections */}
              <Paper sx={{ p: 4, border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
                <SubSectionTitle>Financial Impact Projections</SubSectionTitle>
                <TableContainer sx={{ mt: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#1976d2' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Performance Metric</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Year 1</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Year 2</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Year 3</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {financialProjections.map((projection, index) => (
                        <TableRow
                          key={index}
                          sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f8f9fa' } }}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>{projection.metric}</TableCell>
                          <TableCell sx={{ color: '#4caf50', fontWeight: 500 }}>{projection.year1}</TableCell>
                          <TableCell sx={{ color: '#1976d2', fontWeight: 500 }}>{projection.year2}</TableCell>
                          <TableCell sx={{ color: '#7b1fa2', fontWeight: 500 }}>{projection.year3}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 4, p: 3, backgroundColor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                    Investment Summary
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    The recommended strategic initiatives represent a comprehensive approach to value creation,
                    targeting revenue optimization, operational efficiency, and market positioning enhancement.
                    The projected financial returns demonstrate strong investment viability with manageable risk exposure
                    and clear pathways to sustained competitive advantage.
                  </Typography>
                </Box>
              </Paper>
            </>
          );
        } catch (error) {
          console.error('Error rendering Recommendations component:', error);
          return (
            <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Recommend sx={{ fontSize: 32, color: '#1976d2', mr: 2 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    Error Rendering Recommendations
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    An error occurred while rendering the recommendations component.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        }
      })()}
    </Section>
  );
};
